import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import Loader from 'Components/Forms/Loader';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import useUserData from 'FeatureModules/Auth/hooks/useUserData';
import { REQUEST_PLAN_UPGRADE } from 'graphql/secure/mutations';
import { GET_COMPANY_DETAILS } from 'graphql/secure/queries';
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  ECustomerPlan,
  TCustomerPlanType,
  ICompany,
} from 'FeatureModules/Auth/types/company.types';
import { useFirebase } from './FirebaseContext';
import { secureCachePersistor } from 'graphql/secure/SecureApolloProvider';
import CompanyPlanDialog from 'FeatureModules/Setting/Components/Dialogs/CompanyPlanDialog';

interface ICompanyPlanContextValues {
  open: boolean;
  company: ICompany;
  setOpen: (isOpen: boolean) => void;
  daysLeftInTrial: number;
  plan: TCustomerPlanType;
}

const CompanyPlanContext = createContext<ICompanyPlanContextValues>({
  open: false,
  company: {} as ICompany,
  setOpen: () => {
    console.error('unimplemented context');
  },
  daysLeftInTrial: 0,
  plan: ECustomerPlan.INACTIVE,
});

const useCompanyPlanContext = () => useContext(CompanyPlanContext);

const CompanyPlanProvider: FC<PropsWithChildren> = ({ children }) => {
  useQuery(GET_COMPANY_DETAILS);
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { company } = useUserData();
  const plan: TCustomerPlanType = company?.planDetails?.planType || ECustomerPlan.INACTIVE;
  const { logEvent } = useLogEvent();
  const isInactive = company?.planDetails?.planType === ECustomerPlan.INACTIVE;
  const { logout } = useFirebase();
  // https://github.com/apollographql/apollo-client/issues/4970
  // The functions lose their this context when you destructure them from the instantiated Apollo Client because they are currently written as class methods
  const client = useApolloClient();

  const isUpgradeRequested: boolean =
    company?.planDetails?.upgradeRequested !== undefined
      ? company.planDetails.upgradeRequested
      : false;

  const isCompanyPlanExpired: boolean =
    company?.planDetails?.expired !== undefined ? company?.planDetails?.expired : true;

  const daysLeftInTrial: number =
    company?.planDetails?.daysLeftInTrial !== undefined ? company?.planDetails?.daysLeftInTrial : 0;

  const [requestSent, setRequestSent] = useState(isUpgradeRequested);
  const [open, setOpen] = useState<boolean>(false);

  const [requestUpgrade] = useMutation(REQUEST_PLAN_UPGRADE, {
    onError: () => {
      setErrorMessage('Something went wrong requesting your upgrade. Please contact our support.');
    },
    onCompleted: () => {
      logEvent('REQUEST_PLAN_UPGRADE', {
        days_left_in_trial: daysLeftInTrial,
      });
      setSuccessMessage('We received your upgrade request and will get back to you soon.');
      setRequestSent(true);
    },
  });

  const handleClose = () => {
    if (!isCompanyPlanExpired) {
      setOpen(false);
    }
  };

  const handleSubmit = async (values: { message: string }) => {
    await requestUpgrade({
      variables: {
        ...values,
      },
    });
  };

  const handleLogout = async () => {
    await client.clearStore();
    await secureCachePersistor.purge();
    logout();
  };

  useEffect(() => {
    if (company && (isCompanyPlanExpired || isInactive)) {
      setOpen(true);
    }
  }, [company, isCompanyPlanExpired, isInactive]);

  if (!company) return <Loader />;

  return (
    <CompanyPlanContext.Provider
      value={{
        open,
        company,
        daysLeftInTrial,
        plan,
        setOpen,
      }}
    >
      <CompanyPlanDialog
        open={open}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
        handleLogout={handleLogout}
        requestSent={requestSent}
        daysLeftInTrial={daysLeftInTrial}
        expired={isCompanyPlanExpired}
        isInactive={isInactive}
      />
      {children}
    </CompanyPlanContext.Provider>
  );
};

export { CompanyPlanProvider, useCompanyPlanContext };

export default CompanyPlanContext;
